import { API_BASE_URL } from "src/config/apiBase";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { decryptToken } from "../utils/getToken";
import { getSessionMainId } from "../utils/getCookie";
import "./StudentAttendance.css";

function localISODate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function serializeSnapshot(list) {
  return JSON.stringify(
    list
      .map((s) => ({
        id: s.admissionPk,
        status: s.status,
        remark: s.remark || "",
      }))
      .sort((a, b) => a.id - b.id)
  );
}

export default function StudentAttendance() {
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [date, setDate] = useState(() => localISODate());
  const [students, setStudents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState("name");
  const [focusIndex, setFocusIndex] = useState(0);
  const [baseline, setBaseline] = useState("");
  const [autoSave, setAutoSave] = useState(false);

  const [view, setView] = useState("mark");
  const [histFrom, setHistFrom] = useState(() => {
    const t = localISODate();
    const d = new Date(`${t}T12:00:00`);
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [histTo, setHistTo] = useState(() => localISODate());
  const [histSummary, setHistSummary] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histRoster, setHistRoster] = useState([]);
  const [histStudentPk, setHistStudentPk] = useState("");
  const [histRecords, setHistRecords] = useState([]);
  const [histStudentMeta, setHistStudentMeta] = useState(null);
  const [histStudentLoading, setHistStudentLoading] = useState(false);

  const rowRefs = useRef([]);

  const dirty = useMemo(() => {
    if (!loaded || !students.length) return false;
    return serializeSnapshot(students) !== baseline;
  }, [loaded, students, baseline]);

  const loadBatches = useCallback(async () => {
    try {
      const res = await fetch(`${url}/admission/batches`, { headers });
      const j = await res.json();
      setBatches(Array.isArray(j?.batches) ? j.batches : []);
    } catch (e) {
      console.error(e);
      setBatches([]);
    }
  }, [url, headers]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    if (view !== "history" || !batchId) {
      setHistRoster([]);
      return undefined;
    }
    const bid = parseInt(batchId, 10);
    if (Number.isNaN(bid)) {
      setHistRoster([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${url}/admission/student-attendance/roster?batch_id=${bid}`, {
          headers,
        });
        const j = await res.json();
        if (cancelled) return;
        if (res.ok && !j.isError) {
          setHistRoster(Array.isArray(j.students) ? j.students : []);
        } else {
          setHistRoster([]);
        }
      } catch {
        if (!cancelled) setHistRoster([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [view, batchId, url, headers]);

  const loadHistorySummary = useCallback(async () => {
    const bid = parseInt(batchId, 10);
    if (Number.isNaN(bid)) {
      toast.warn("Select a batch first");
      return;
    }
    if (!getSessionMainId()) {
      toast.error("Session expired");
      return;
    }
    setHistLoading(true);
    try {
      const qs = new URLSearchParams({
        batch_id: String(bid),
        from: histFrom,
        to: histTo,
      });
      const res = await fetch(`${url}/admission/student-attendance/history/summary?${qs}`, {
        headers,
      });
      const data = await res.json();
      if (!res.ok || data.isError) {
        toast.error(data.message || "Failed to load history");
        setHistSummary([]);
        return;
      }
      setHistSummary(Array.isArray(data.days) ? data.days : []);
    } catch (e) {
      console.error(e);
      toast.error("Network error");
      setHistSummary([]);
    } finally {
      setHistLoading(false);
    }
  }, [batchId, histFrom, histTo, url, headers]);

  const loadStudentHistory = useCallback(async () => {
    const bid = parseInt(batchId, 10);
    const pk = parseInt(histStudentPk, 10);
    if (Number.isNaN(bid)) {
      toast.warn("Select a batch first");
      return;
    }
    if (Number.isNaN(pk)) {
      toast.warn("Select a student");
      return;
    }
    if (!getSessionMainId()) {
      toast.error("Session expired");
      return;
    }
    setHistStudentLoading(true);
    try {
      const qs = new URLSearchParams({
        batch_id: String(bid),
        admission_id: String(pk),
        from: histFrom,
        to: histTo,
      });
      const res = await fetch(`${url}/admission/student-attendance/history/student?${qs}`, {
        headers,
      });
      const data = await res.json();
      if (!res.ok || data.isError) {
        toast.error(data.message || "Failed to load student history");
        setHistRecords([]);
        setHistStudentMeta(null);
        return;
      }
      setHistStudentMeta(data.student || null);
      setHistRecords(Array.isArray(data.records) ? data.records : []);
    } catch (e) {
      console.error(e);
      toast.error("Network error");
      setHistRecords([]);
      setHistStudentMeta(null);
    } finally {
      setHistStudentLoading(false);
    }
  }, [batchId, histStudentPk, histFrom, histTo, url, headers]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = students;
    if (q) {
      rows = rows.filter((s) => {
        const n = (s.name || "").toLowerCase();
        const roll = (s.student_id || "").toLowerCase();
        const adm = (s.admission_id || "").toLowerCase();
        return n.includes(q) || roll.includes(q) || adm.includes(q);
      });
    }
    const out = [...rows];
    if (sortMode === "roll") {
      out.sort((a, b) => {
        const ra = (a.student_id || "").toLowerCase();
        const rb = (b.student_id || "").toLowerCase();
        const c = ra.localeCompare(rb, undefined, { numeric: true });
        if (c !== 0) return c;
        return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
      });
    } else {
      out.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
      );
    }
    return out;
  }, [students, search, sortMode]);

  useEffect(() => {
    if (focusIndex >= filteredSorted.length) {
      setFocusIndex(Math.max(0, filteredSorted.length - 1));
    }
  }, [filteredSorted.length, focusIndex]);

  const liveCounts = useMemo(() => {
    const c = { Present: 0, Absent: 0, Late: 0 };
    for (const s of students) {
      if (c[s.status] != null) c[s.status] += 1;
    }
    return c;
  }, [students]);

  const updateStudent = useCallback((admissionPk, patch) => {
    setStudents((prev) =>
      prev.map((s) => (s.admissionPk === admissionPk ? { ...s, ...patch } : s))
    );
  }, []);

  const setAllStatus = useCallback((status) => {
    setStudents((prev) => prev.map((s) => ({ ...s, status })));
  }, []);

  const loadStudents = useCallback(async () => {
    const bid = parseInt(batchId, 10);
    if (Number.isNaN(bid)) {
      toast.warn("Select a batch first");
      return;
    }
    const mainId = getSessionMainId();
    if (!mainId) {
      toast.error("Session expired");
      return;
    }
    setLoading(true);
    try {
      const qs = new URLSearchParams({ batch_id: String(bid), date });
      const res = await fetch(`${url}/admission/student-attendance/sheet?${qs}`, { headers });
      const data = await res.json();
      if (!res.ok || data.isError) {
        toast.error(data.message || "Failed to load");
        return;
      }

      if (data.editMode) {
        const r = await Swal.fire({
          title: "Attendance already exists",
          text: "Load saved marks and continue editing?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Edit",
          cancelButtonText: "Cancel",
        });
        if (!r.isConfirmed) {
          setLoaded(false);
          setStudents([]);
          setEditMode(false);
          return;
        }
      }

      const list = Array.isArray(data.students) ? data.students : [];
      setStudents(list);
      setBaseline(serializeSnapshot(list));
      setEditMode(Boolean(data.editMode));
      setLoaded(true);
      setFocusIndex(0);
    } catch (e) {
      console.error(e);
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  }, [batchId, date, url, headers]);

  const saveAttendance = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    const bid = parseInt(batchId, 10);
    if (Number.isNaN(bid) || !students.length) {
      if (!silent) toast.warn("Load students first");
      return;
    }
    const mainId = getSessionMainId();
    if (!mainId) {
      toast.error("Session expired");
      return;
    }
    setSaving(true);
    try {
      const entries = students.map((s) => ({
        admission_id: s.admissionPk,
        status: s.status,
        remark: s.remark || "",
      }));
      const res = await fetch(`${url}/admission/student-attendance/save`, {
        method: "POST",
        headers,
        body: JSON.stringify({ batch_id: bid, date, entries }),
      });
      const data = await res.json();
      if (!res.ok || data.isError) {
        toast.error(data.message || "Save failed");
        return;
      }
      setBaseline(serializeSnapshot(students));
      setEditMode(true);
      if (!silent) {
        toast.success("Attendance saved");
      }
    } catch (e) {
      console.error(e);
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  }, [batchId, date, students, url, headers]);

  useEffect(() => {
    if (!autoSave || !dirty || !loaded || saving) return undefined;
    const t = setInterval(() => {
      saveAttendance({ silent: true });
    }, 12000);
    return () => clearInterval(t);
  }, [autoSave, dirty, loaded, saving, saveAttendance]);

  const focusedPk = filteredSorted[focusIndex]?.admissionPk;

  useEffect(() => {
    const el = rowRefs.current[focusIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [focusIndex, filteredSorted.length]);

  useEffect(() => {
    const tag = (t) => (t && t.tagName ? t.tagName.toLowerCase() : "");
    const isFormField = (el) => {
      if (!el || el === document.body) return false;
      const t = tag(el);
      if (t === "input" || t === "textarea" || t === "select") return true;
      if (el.isContentEditable) return true;
      return isFormField(el.parentElement);
    };

    const onKey = (e) => {
      if (isFormField(e.target)) {
        if (e.key === "Escape") {
          e.target.blur?.();
        }
        return;
      }
      if (view !== "mark" || !loaded || !filteredSorted.length) return;

      const move = (delta) => {
        e.preventDefault();
        setFocusIndex((i) => {
          const n = filteredSorted.length;
          if (!n) return 0;
          return Math.min(n - 1, Math.max(0, i + delta));
        });
      };

      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        move(1);
        return;
      }
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        move(-1);
        return;
      }

      const pk = filteredSorted[focusIndex]?.admissionPk;
      if (pk == null) return;

      const setStatus = (status) => {
        e.preventDefault();
        updateStudent(pk, { status });
      };

      if (e.key === "p" || e.key === "P") setStatus("Present");
      else if (e.key === "a" || e.key === "A") setStatus("Absent");
      else if (e.key === "l" || e.key === "L") setStatus("Late");
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, loaded, filteredSorted, focusIndex, updateStudent]);

  const batchLabel = (b) =>
    b.scheduleSummary ? `${b.name} — ${b.scheduleSummary}` : b.name || `Batch #${b.id}`;

  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const fn = () => setMobile(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  return (
    <div className="sa-page">
      <div className="sa-inner">
        <div className="sa-view-tabs" role="tablist" aria-label="Attendance mode">
          <button
            type="button"
            role="tab"
            aria-selected={view === "mark"}
            className={`sa-tab ${view === "mark" ? "sa-tab--active" : ""}`}
            onClick={() => setView("mark")}
          >
            Mark attendance
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "history"}
            className={`sa-tab ${view === "history" ? "sa-tab--active" : ""}`}
            onClick={() => setView("history")}
          >
            History
          </button>
        </div>

        {view === "history" && (
          <div className="sa-history-panel">
            <p className="sa-history-intro">
              Review saved attendance by day for a batch, or open one student&apos;s timeline for the
              selected date range (up to one year).
            </p>
            <div className="sa-history-toolbar">
              <div className="sa-field">
                <label htmlFor="sa-hist-batch">Batch</label>
                <select
                  id="sa-hist-batch"
                  value={batchId}
                  onChange={(e) => {
                    setBatchId(e.target.value);
                    setHistStudentPk("");
                    setHistSummary([]);
                    setHistRecords([]);
                    setHistStudentMeta(null);
                  }}
                >
                  <option value="">Select batch…</option>
                  {batches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {batchLabel(b)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sa-field">
                <label htmlFor="sa-hist-from">From</label>
                <input
                  id="sa-hist-from"
                  type="date"
                  value={histFrom}
                  onChange={(e) => setHistFrom(e.target.value)}
                />
              </div>
              <div className="sa-field">
                <label htmlFor="sa-hist-to">To</label>
                <input
                  id="sa-hist-to"
                  type="date"
                  value={histTo}
                  onChange={(e) => setHistTo(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="sa-btn sa-btn--outline"
                onClick={loadHistorySummary}
                disabled={histLoading || !batchId}
              >
                {histLoading ? "…" : "Load summary"}
              </button>
            </div>

            <div className="sa-history-section">
              <h2 className="sa-history-heading">By day</h2>
              {histLoading && <div className="sa-loader">Loading…</div>}
              {!histLoading && histSummary.length === 0 && (
                <p className="sa-empty sa-history-empty">
                  No rows in this range yet. Choose a batch and tap <strong>Load summary</strong>, or
                  mark attendance on the other tab first.
                </p>
              )}
              {histSummary.length > 0 && (
                <div className="sa-table-wrap sa-history-table-wrap">
                  <table className="sa-table sa-history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Present</th>
                        <th>Absent</th>
                        <th>Late</th>
                        <th>Records</th>
                      </tr>
                    </thead>
                    <tbody>
                      {histSummary.map((row) => (
                        <tr key={row.date}>
                          <td>{row.date}</td>
                          <td>{row.present}</td>
                          <td>{row.absent}</td>
                          <td>{row.late}</td>
                          <td>{row.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="sa-history-section">
              <h2 className="sa-history-heading">Student timeline</h2>
              <div className="sa-history-toolbar sa-history-toolbar--secondary">
                <div className="sa-field sa-field--grow">
                  <label htmlFor="sa-hist-student">Student</label>
                  <select
                    id="sa-hist-student"
                    value={histStudentPk}
                    onChange={(e) => setHistStudentPk(e.target.value)}
                    disabled={!batchId || !histRoster.length}
                  >
                    <option value="">
                      {!batchId
                        ? "Select a batch first"
                        : histRoster.length === 0
                          ? "No students in batch"
                          : "Select student…"}
                    </option>
                    {histRoster.map((s) => (
                      <option key={s.admissionPk} value={s.admissionPk}>
                        {s.name}
                        {(s.student_id || s.admission_id) &&
                          ` (${[s.student_id, s.admission_id].filter(Boolean).join(" · ")})`}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="sa-btn sa-btn--outline"
                  onClick={loadStudentHistory}
                  disabled={histStudentLoading || !batchId || !histStudentPk}
                >
                  {histStudentLoading ? "…" : "Load student history"}
                </button>
              </div>
              {histStudentMeta && (
                <p className="sa-history-student-meta">
                  <strong>{histStudentMeta.name}</strong>
                  {histStudentMeta.admission_id && ` · ${histStudentMeta.admission_id}`}
                </p>
              )}
              {histStudentLoading && <div className="sa-loader">Loading…</div>}
              {!histStudentLoading && histRecords.length === 0 && histStudentMeta && (
                <p className="sa-empty sa-history-empty">No attendance records in this range.</p>
              )}
              {histRecords.length > 0 && (
                <div className="sa-table-wrap sa-history-table-wrap">
                  <table className="sa-table sa-history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Remark</th>
                        <th>Marked by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {histRecords.map((r) => (
                        <tr key={r.id}>
                          <td>{r.date}</td>
                          <td>
                            <span className={`sa-hist-status sa-hist-status--${String(r.status).toLowerCase()}`}>
                              {r.status}
                            </span>
                          </td>
                          <td>{r.remark || "—"}</td>
                          <td>{r.marked_by || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {view === "mark" && (
          <>
        <div className="sa-sticky-header">
          <div className="sa-header-row">
            <div className="sa-header-left">
              <div className="sa-field">
                <label htmlFor="sa-batch">Batch</label>
                <select
                  id="sa-batch"
                  value={batchId}
                  onChange={(e) => {
                    setBatchId(e.target.value);
                    setLoaded(false);
                    setStudents([]);
                  }}
                >
                  <option value="">Select batch…</option>
                  {batches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {batchLabel(b)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sa-field">
                <label htmlFor="sa-date">Date</label>
                <input
                  id="sa-date"
                  type="date"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setLoaded(false);
                    setStudents([]);
                  }}
                />
              </div>
              <button
                type="button"
                className="sa-btn sa-btn--outline"
                onClick={loadStudents}
                disabled={loading || !batchId}
              >
                {loading ? "…" : "Load students"}
              </button>
            </div>
            <div className="sa-header-right">
              <div className="sa-auto-wrap">
                <input
                  id="sa-autosave"
                  type="checkbox"
                  checked={autoSave}
                  onChange={(e) => setAutoSave(e.target.checked)}
                />
                <label htmlFor="sa-autosave">Auto-save ~12s</label>
              </div>
            </div>
          </div>

          <div className="sa-toolbar-second">
            <div className="sa-counts">
              <span data-k="present">Present: {liveCounts.Present}</span>
              <span data-k="absent">Absent: {liveCounts.Absent}</span>
              <span data-k="late">Late: {liveCounts.Late}</span>
            </div>
            <div className="sa-toolbar-actions">
              <button
                type="button"
                className="sa-btn sa-btn--success sa-bulk-desktop"
                onClick={() => setAllStatus("Present")}
                disabled={!loaded || !students.length}
              >
                Mark all present
              </button>
              <button
                type="button"
                className="sa-btn sa-btn--danger sa-bulk-desktop"
                onClick={() => setAllStatus("Absent")}
                disabled={!loaded || !students.length}
              >
                Mark all absent
              </button>
              <button
                type="button"
                className="sa-btn sa-btn--primary sa-btn--emphasis"
                onClick={() => saveAttendance({ silent: false })}
                disabled={saving || !loaded || !students.length}
              >
                {saving ? "Saving…" : "Save attendance"}
              </button>
            </div>
          </div>
        </div>

        {editMode && loaded && (
          <div className="sa-edit-banner" role="status">
            Edit mode active — attendance for this date was saved before
          </div>
        )}

        {loading && <div className="sa-loader">Loading…</div>}

        {!loading && loaded && students.length === 0 && (
          <div className="sa-empty">No students found in this batch</div>
        )}

        {loaded && students.length > 0 && (
          <>
            <p className="sa-kbd-hint">
              Keyboard: ↑ ↓ ← → navigate rows · P Present · A Absent · L Late · Esc leaves remark field
            </p>
            <div className="sa-search">
              <div className="sa-search-wrap">
                <Search size={18} />
                <input
                  type="search"
                  placeholder="Search student…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search students"
                />
              </div>
            </div>
            <div className="sa-group-toggle">
              <button
                type="button"
                className={`sa-chip ${sortMode === "name" ? "sa-chip--on" : ""}`}
                onClick={() => setSortMode("name")}
              >
                A–Z name
              </button>
              <button
                type="button"
                className={`sa-chip ${sortMode === "roll" ? "sa-chip--on" : ""}`}
                onClick={() => setSortMode("roll")}
              >
                Roll / ID
              </button>
            </div>

            {!mobile && (
              <div className="sa-table-wrap">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Status</th>
                      <th>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSorted.map((s, idx) => (
                      <tr
                        key={s.admissionPk}
                        ref={(el) => {
                          rowRefs.current[idx] = el;
                        }}
                        className={focusedPk === s.admissionPk ? "sa-row--focused" : ""}
                      >
                        <td className="sa-name-cell">
                          {s.name}
                          {(s.student_id || s.admission_id) && (
                            <span className="sa-roll">
                              {[s.student_id, s.admission_id].filter(Boolean).join(" · ")}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="sa-toggle-group" role="group" aria-label="Attendance status">
                            {["Present", "Absent", "Late"].map((st) => (
                              <button
                                key={st}
                                type="button"
                                className={`sa-toggle sa-toggle--${st.toLowerCase()} ${
                                  s.status === st ? "sa-toggle--active" : ""
                                }`}
                                onClick={() => updateStudent(s.admissionPk, { status: st })}
                              >
                                {st}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td>
                          <input
                            className="sa-remark"
                            placeholder="Optional"
                            value={s.remark || ""}
                            onChange={(e) =>
                              updateStudent(s.admissionPk, { remark: e.target.value })
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredSorted.length === 0 && (
                  <div className="sa-empty">No matches for your search</div>
                )}
              </div>
            )}

            {mobile && (
              <div className="sa-cards">
                {filteredSorted.map((s, idx) => (
                  <div
                    key={s.admissionPk}
                    ref={(el) => {
                      rowRefs.current[idx] = el;
                    }}
                    className={`sa-card ${focusedPk === s.admissionPk ? "sa-card--focused" : ""}`}
                  >
                    <div className="sa-card-title">{s.name}</div>
                    {(s.student_id || s.admission_id) && (
                      <div className="sa-roll" style={{ marginBottom: 8 }}>
                        {[s.student_id, s.admission_id].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div className="sa-toggle-group" role="group" aria-label="Attendance status">
                      {["Present", "Absent", "Late"].map((st) => (
                        <button
                          key={st}
                          type="button"
                          className={`sa-toggle sa-toggle--${st.toLowerCase()} ${
                            s.status === st ? "sa-toggle--active" : ""
                          }`}
                          onClick={() => updateStudent(s.admissionPk, { status: st })}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                    <input
                      className="sa-remark"
                      placeholder="Remark (optional)"
                      value={s.remark || ""}
                      onChange={(e) => updateStudent(s.admissionPk, { remark: e.target.value })}
                    />
                  </div>
                ))}
                {filteredSorted.length === 0 && (
                  <div className="sa-empty">No matches for your search</div>
                )}
              </div>
            )}
          </>
        )}

        {!loaded && !loading && (
          <p className="sa-empty" style={{ marginTop: 24 }}>
            Choose a batch and date, then tap <strong>Load students</strong>.
          </p>
        )}
          </>
        )}
      </div>

      {mobile && view === "mark" && loaded && students.length > 0 && (
        <div className="sa-mobile-bar">
          <button
            type="button"
            className="sa-btn sa-btn--success"
            onClick={() => setAllStatus("Present")}
          >
            All present
          </button>
          <button
            type="button"
            className="sa-btn sa-btn--primary sa-btn--emphasis"
            onClick={() => saveAttendance({ silent: false })}
            disabled={saving}
          >
            {saving ? "…" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}
